'use client';

import Link from 'next/link';
import { OPEN_PRIVACY_SETTINGS_EVENT } from '@/lib/privacy/company-analytics-consent';

const FOOTER_RESOURCES = [
  { label: 'Search', href: '/hub/' },
  { label: 'Providers', href: '/providers/' },
  { label: 'Ports', href: '/hub/ports/' },
  { label: 'Index', href: '/hub/index/' },
];

const FOOTER_SITE_LINKS = [
  { label: 'For Logistics Clients', href: '/' },
  { label: 'For Logistics Providers', href: '/logistics-providers/' },
  { label: 'Client pricing', href: '/pricing/' },
  { label: 'Provider pricing', href: '/logistics-providers/pricing/' },
  { label: 'Contact Us', href: '/contact/' },
];

export const Footer = () => {
  return (
    <footer className="bg-gray-50 border-t mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 flex flex-col md:flex-row gap-8">
          {/* Brand Column */}
          <div className="flex-1 max-w-md">
            <Link href="/" className="flex items-center space-x-2 mb-4">
              <img src="/icon.svg" alt="RatesAgent.AI" className="w-8 h-8" />
              <span className="text-xl text-gray-800">RatesAgent.AI</span>
            </Link>
            <p className="text-gray-600 text-sm mb-4">
              Connect with freight forwarders, carriers, and logistics
              providers worldwide.
            </p>
            {/* Social Links */}
            <a
              href="https://www.linkedin.com/company/ratesagentai/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              aria-label="LinkedIn"
            >
              <svg
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>

          {/* Link columns */}
          <div className="flex gap-16">
            {/* Resources Column */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                The HUB
              </h3>
              <ul className="space-y-3">
                {FOOTER_RESOURCES.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Site Links Column */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Site Links
              </h3>
              <ul className="space-y-3">
                {FOOTER_SITE_LINKS.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 py-6">
          <div className="flex flex-col items-start md:flex-row gap-8">
            <div className="flex-1 max-w-md flex flex-col space-y-1">
              <p className="text-gray-600 text-sm">
                © {new Date().getFullYear()} RatesAgent.AI. All rights reserved.
              </p>
              <p className="text-gray-500 text-xs">
                Powered by{' '}
                <a
                  href="https://adexin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-gray-700 transition-colors"
                >
                  Adexin
                </a>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm leading-5">
              <Link
                href="/privacy/"
                className="inline-flex items-center whitespace-nowrap text-gray-600 hover:text-gray-900 transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms/"
                className="inline-flex items-center whitespace-nowrap text-gray-600 hover:text-gray-900 transition-colors"
              >
                Terms of Service
              </Link>
              <button
                type="button"
                className="inline-flex items-center whitespace-nowrap text-gray-600 hover:text-gray-900 transition-colors"
                onClick={() => {
                  window.dispatchEvent(new Event(OPEN_PRIVACY_SETTINGS_EVENT));
                }}
              >
                <span>Privacy settings</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
